import { useId, useState } from "react";

/**
 * "Is my ranking report showing an improvement?" checker.
 * Placement: blog post, section "How Long It Takes and How to Tell It's Working",
 * directly after the paragraph that starts "Read ranking numbers carefully."
 * Uses shadcn/Tailwind theme tokens so it inherits the site's colours.
 */

function toPosition(value: string): number | null {
  const n = Number(value.trim());
  return Number.isFinite(n) && n >= 1 && n <= 100 ? n : null;
}

function band(pos: number): string {
  if (pos <= 3) return "in the top 3";
  if (pos <= 10) return "in the top 10";
  if (pos <= 20) return "between 11 and 20";
  return "below 20";
}

const fmt = (n: number) => (Number.isInteger(n) ? String(n) : n.toFixed(1));

export default function RankingChangeChecker() {
  const beforeId = useId();
  const afterId = useId();
  const [before, setBefore] = useState("");
  const [after, setAfter] = useState("");

  const b = toPosition(before);
  const a = toPosition(after);
  const invalid = (before !== "" && b === null) || (after !== "" && a === null);

  let verdict: { heading: string; detail: string } | null = null;
  if (b !== null && a !== null) {
    const diff = b - a;
    if (diff > 0) {
      verdict = {
        heading: `Improved by ${fmt(diff)} position${diff === 1 ? "" : "s"}`,
        detail: `The page moved from ${band(b)} to ${band(a)}. A lower number means a higher spot on the page.`,
      };
    } else if (diff < 0) {
      verdict = {
        heading: `Declined by ${fmt(-diff)} position${diff === -1 ? "" : "s"}`,
        detail: `The page moved from ${band(b)} to ${band(a)}. A higher number means the page dropped further down.`,
      };
    } else {
      verdict = { heading: "No change", detail: `The page is still ${band(a)}.` };
    }
  }

  const inputClass =
    "mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-base focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2";

  return (
    <section
      aria-labelledby="ranking-check-title"
      className="not-prose my-10 rounded-xl border border-border bg-card p-6 text-card-foreground sm:p-8"
    >
      <h3 id="ranking-check-title" className="text-xl font-semibold">
        Is your ranking report showing an improvement?
      </h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Enter the average position from your report or Search Console, before and after.
      </p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2">
        <div>
          <label htmlFor={beforeId} className="text-sm font-medium">
            Position before
          </label>
          <input
            id={beforeId}
            inputMode="decimal"
            placeholder="e.g. 29"
            value={before}
            onChange={(e) => setBefore(e.target.value)}
            className={inputClass}
          />
        </div>
        <div>
          <label htmlFor={afterId} className="text-sm font-medium">
            Position after
          </label>
          <input
            id={afterId}
            inputMode="decimal"
            placeholder="e.g. 34"
            value={after}
            onChange={(e) => setAfter(e.target.value)}
            className={inputClass}
          />
        </div>
      </div>

      <div aria-live="polite" className="mt-6">
        {invalid ? (
          <p className="text-sm text-muted-foreground">Enter positions between 1 and 100.</p>
        ) : verdict ? (
          <div className="rounded-lg bg-muted p-5">
            <p className="text-lg font-semibold">{verdict.heading}</p>
            <p className="mt-2 text-base text-muted-foreground">{verdict.detail}</p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">Enter both positions to check.</p>
        )}
      </div>
    </section>
  );
}
